import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EditDocumentModal = ({ 
  document, 
  onConfirm, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    title: document?.title || document?.name || '',
    description: document?.description || ''
  });

  const descriptionLength = formData.description.length;
  const descriptionTarget = 100;
  const descriptionPercent = Math.min((descriptionLength / descriptionTarget) * 100, 100);
  const isTitleValid = formData.title?.trim().length > 0;

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfirm = () => {
    if (!isTitleValid) {
      alert('Please provide a title');
      return;
    }
    onConfirm({
      ...document,
      title: formData.title.trim(),
      description: formData.description.trim()
    });
  };

  const suggestions = [
    {
      icon: 'Target',
      title: 'Be Specific',
      description: 'Include specific course names, certifications, or key achievements'
    },
    {
      icon: 'BookOpen',
      title: 'Add Context',
      description: 'Explain why this document matters and what skills it demonstrates'
    },
    {
      icon: 'Brain',
      title: 'Include Details',
      description: 'The more detailed your description, the better AI recommendations'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card dark:bg-gray-800 border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Edit Document</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update the title and description to improve AI recommendations
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Document Info */}
            <div className="border border-border rounded-lg p-4 bg-muted/30 dark:bg-gray-700/30">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">File Name</p>
              <p className="font-medium text-foreground truncate">{document?.name || document?.title}</p>
            </div>

            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title <span className="text-error">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Private Equity and Venture Capital Certificate"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background dark:bg-gray-900 text-foreground dark:text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {!isTitleValid && (
                <p className="text-xs text-error mt-1">Title is required</p>
              )}
            </div>

            {/* Description Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground">
                  Description (optional)
                </label>
                <span className={`text-xs ${descriptionLength > 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                  {descriptionLength} characters
                </span>
              </div>
              <textarea
                placeholder="Describe what this document represents, skills it demonstrates, and any other relevant details..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background dark:bg-gray-900 text-foreground dark:text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              
              {/* Progress Bar */}
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all"
                    style={{ width: `${descriptionPercent}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${descriptionLength >= descriptionTarget ? 'text-success' : 'text-muted-foreground'}`}>
                  {Math.max(0, descriptionTarget - descriptionLength)} chars to ideal
                </span>
              </div>
            </div>

            {/* Suggestions Section */}
            <div className="border border-border rounded-lg p-4 bg-primary/5 dark:bg-primary/10">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Lightbulb" size={18} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground text-sm">Tips for Better AI Recommendations</h3>
              </div>
              <div className="space-y-3">
                {suggestions.map((suggestion, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name={suggestion.icon} size={14} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{suggestion.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{suggestion.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Message */}
            <div className="p-3 bg-accent/10 dark:bg-accent/5 rounded-lg border border-accent/20">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-accent">💡 Pro Tip:</span> The more detailed your document descriptions are, the more accurate and personalized your AI university recommendations will be.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card dark:bg-gray-800 border-t border-border p-6 flex items-center gap-3 justify-end">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!isTitleValid}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditDocumentModal;
